/**
 * Catalog Registry
 * Central registry for chat UI components
 * 
 * This registry provides:
 * - Component registration and lookup
 * - Fallback component handling
 * - Extensibility for future custom components
 */

import { COMPONENT_TYPES } from '../constants';

// Import catalog components
import AssistantMessage from './AssistantMessage';
import StepIndicator from './StepIndicator';
import SuggestedPrompt, { SuggestedPromptGroup } from './SuggestedPrompt';
import UserMessage from './UserMessage';

// ============================================================================
// Registry Storage
// ============================================================================

/** @type {Map<string, Object>} Component registry */
const registry = new Map();

// ============================================================================
// Registration
// ============================================================================

/**
 * Register a component in the catalog
 * @param {string} type - Component type (from COMPONENT_TYPES)
 * @param {React.ComponentType} component - React component
 * @param {Object} options - Additional options (defaultProps, etc.)
 */
export const register = (type, component, options = {}) => {
    if (!type || !component) {
        console.warn('Catalog: Invalid registration - type and component required');
        return;
    }

    registry.set(type, {
        component,
        type,
        defaultProps: options.defaultProps || {},
        name: component.displayName || component.name || type
    });

    console.log(`Catalog: Registered component "${type}"`);
};

/**
 * Register multiple components at once
 * @param {Object} components - Object mapping type to component
 */
export const registerAll = (components) => {
    Object.entries(components).forEach(([type, component]) => {
        register(type, component);
    });
};

/**
 * Unregister a component from the catalog
 * @param {string} type - Component type to remove
 */
export const unregister = (type) => {
    if (registry.has(type)) {
        registry.delete(type);
        console.log(`Catalog: Unregistered component "${type}"`);
    }
};

// ============================================================================
// Lookup
// ============================================================================

/**
 * Get a component from the catalog by type
 * @param {string} type - Component type
 * @returns {React.ComponentType|null} Component or null if not found
 */
export const getComponent = (type) => {
    const entry = registry.get(type);
    return entry ? entry.component : null;
};

/**
 * Check if a component type is registered
 * @param {string} type - Component type
 * @returns {boolean}
 */
export const isRegistered = (type) => {
    return registry.has(type);
};

/**
 * Get all registered component types
 * @returns {Array<string>}
 */
export const getAllTypes = () => {
    return Array.from(registry.keys());
};

// ============================================================================
// Fallback Component
// ============================================================================

/**
 * Default fallback component for unknown types
 */
const FallbackComponent = ({ type, data, children }) => {
    if (process.env.NODE_ENV === 'development') {
        return (
            <div style={{
                padding: '12px',
                margin: '8px 0',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace'
            }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    ⚠️ Unknown component type: "{type}"
                </div>
                <details>
                    <summary>Data</summary>
                    <pre style={{ margin: '8px 0', overflow: 'auto', maxHeight: '200px' }}>
                        {JSON.stringify(data, null, 2)}
                    </pre>
                </details>
                {children}
            </div>
        );
    }

    // In production, render nothing
    return null;
};

// ============================================================================
// Component Resolution
// ============================================================================

/**
 * Resolve a component for rendering
 * Returns the component and merged props
 * @param {string} type - Component type
 * @param {Object} props - Provided props
 * @returns {Object} { Component, props, found }
 */
export const resolve = (type, props = {}) => {
    const entry = registry.get(type);

    if (entry) {
        return {
            Component: entry.component,
            props: { ...entry.defaultProps, ...props },
            found: true,
            type
        };
    }

    // Return fallback
    return {
        Component: FallbackComponent,
        props: { type, data: props.data, ...props },
        found: false,
        type: 'unknown'
    };
};

// Alias for backward compatibility
export const resolveComponent = resolve;
export const hasComponent = isRegistered;

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Log registry contents to console
 */
export const debugLog = () => {
    console.group('📚 Catalog Registry');
    console.log('Total components:', registry.size);
    console.log('Registered types:', getAllTypes());
    console.groupEnd();
};

// ============================================================================
// Export component types for convenience
// ============================================================================

export { COMPONENT_TYPES };

// ============================================================================
// Default Export
// ============================================================================

export default {
    register,
    registerAll,
    unregister,
    getComponent,
    isRegistered,
    getAllTypes,
    resolve,
    resolveComponent,
    hasComponent,
    debugLog,
    COMPONENT_TYPES
};

// ============================================================================
// Auto-register default components
// ============================================================================

register(COMPONENT_TYPES.USER_MESSAGE, UserMessage);
register(COMPONENT_TYPES.ASSISTANT_MESSAGE, AssistantMessage);
register(COMPONENT_TYPES.STEP_INDICATOR, StepIndicator);
register(COMPONENT_TYPES.CHAT_SUGGESTIONS, SuggestedPromptGroup);

// Log that the registry is ready
console.log('Catalog: Registry initialized with all components');

// ============================================================================
// Export components for direct use
// ============================================================================

export {
    AssistantMessage,
    StepIndicator,
    SuggestedPrompt,
    SuggestedPromptGroup, UserMessage
};